import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

interface AuthState {
  user: User | null;
  session: Session | null;
  loading: boolean;
}

class AuthStore {
  private state: AuthState = {
    user: null,
    session: null,
    loading: true
  };

  private listeners = new Set<(state: AuthState) => void>();
  private initialized = false;
  private initPromise: Promise<void> | null = null;
  private authSubscription: any = null;
  private subscriptionCount = 0;

  // Subscribe to auth state changes with deduplication
  subscribe(listener: (state: AuthState) => void) {
    this.listeners.add(listener);
    this.subscriptionCount++;
    
    // Only log if this is the first subscription or significant changes
    if (this.subscriptionCount === 1 || this.subscriptionCount % 5 === 0) {
      console.log(`🔐 [AUTH STORE] Active subscriptions: ${this.subscriptionCount}`);
    }
    
    // Immediately provide current state
    listener(this.state);
    
    // Initialize only once, even with multiple subscribers
    if (!this.initialized && !this.initPromise) {
      this.initPromise = this.initialize();
    }
    
    return () => {
      this.listeners.delete(listener);
      this.subscriptionCount--;
      
      if (this.subscriptionCount === 0) {
        console.log('🔐 [AUTH STORE] All subscriptions removed');
      }
    };
  }

  private async initialize(): Promise<void> {
    if (this.initialized) return;
    
    console.log('🔐 [AUTH STORE] Initializing auth state (single instance)');
    
    try {
      // Set up auth state listener FIRST
      const { data: { subscription } } = supabase.auth.onAuthStateChange(
        async (event, session) => {
          console.log('🔐 [AUTH STORE] Auth state changed:', event, session?.user?.email);
          
          // Handle different auth events
          if (event === 'SIGNED_OUT' || !session) {
            this.updateState({
              user: null,
              session: null,
              loading: false
            });
            this.clearAuthStorage();
            return;
          }

          if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
            const validSession = await this.validateSession(session);
            this.updateState({
              user: validSession?.user || null,
              session: validSession,
              loading: false
            });
            return;
          }

          if (event === 'INITIAL_SESSION') {
            const validSession = await this.validateSession(session);
            this.updateState({
              user: validSession?.user || null,
              session: validSession,
              loading: false
            });
            return;
          }

          this.updateState({
            user: session?.user || null,
            session,
            loading: false
          });
        }
      );

      // Store subscription for cleanup
      this.authSubscription = subscription;

      // THEN check for existing session (only once)
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) {
        console.error('🔐 [AUTH STORE] Error getting session:', error);
        this.updateState({
          user: null,
          session: null,
          loading: false
        });
        return;
      }

      const validSession = await this.validateSession(session);
      this.updateState({
        user: validSession?.user || null,
        session: validSession,
        loading: false
      });

      this.initialized = true;
    } catch (error) {
      console.error('🔐 [AUTH STORE] Initialization error:', error);
      this.updateState({
        user: null,
        session: null,
        loading: false
      });
      this.initialized = true;
    }
  }

  private async validateSession(session: Session | null): Promise<Session | null> {
    if (!session) return null;
    
    try {
      const { data: { user }, error } = await supabase.auth.getUser(session.access_token);
      if (error || !user) {
        console.log('🔐 [AUTH STORE] Session validation failed');
        return null;
      }
      return session;
    } catch (error) {
      console.error('🔐 [AUTH STORE] Session validation error:', error);
      return null;
    }
  }

  private clearAuthStorage() {
    // Clear any additional local storage items related to auth
    localStorage.removeItem('sb-blxigdarvkuivfozquha-auth-token');
    localStorage.removeItem('supabase.auth.token');
    
    // Additional cleanup - clear all possible auth-related storage
    const keysToRemove = Object.keys(localStorage).filter(key => 
      key.includes('supabase') || 
      key.includes('auth') || 
      key.includes('sb-')
    );
    
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
    });
  }

  private updateState(newState: Partial<AuthState>) {
    this.state = { ...this.state, ...newState };
    this.listeners.forEach(listener => listener(this.state));
  }

  // Auth methods
  async signUp(email: string, password: string, userData?: { full_name?: string }) {
    console.log('🔐 [AUTH STORE] Attempting sign up for:', email);
    this.updateState({ loading: true });
    
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: userData,
          emailRedirectTo: `${window.location.origin}/`
        }
      });
      
      this.updateState({ loading: false });
      return { data, error };
    } catch (error) {
      console.error('🔐 [AUTH STORE] Sign up error:', error);
      this.updateState({ loading: false });
      return { data: null, error: error as any };
    }
  }

  async signIn(email: string, password: string) {
    console.log('🔐 [AUTH STORE] Attempting sign in for:', email);
    this.updateState({ loading: true });
    
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      
      if (error) {
        console.error('🔐 [AUTH STORE] Sign in error:', error);
      } else {
        console.log('🔐 [AUTH STORE] Sign in successful');
      }
      
      this.updateState({ loading: false });
      return { data, error };
    } catch (error) {
      console.error('🔐 [AUTH STORE] Sign in error:', error);
      this.updateState({ loading: false });
      return { data: null, error: error as any };
    }
  }

  async signOut() {
    console.log('🔐 [AUTH STORE] Attempting sign out');
    this.updateState({ loading: true });
    
    try {
      // Clear state immediately to prevent UI issues
      this.updateState({
        user: null,
        session: null,
        loading: false
      });
      this.clearAuthStorage();
      
      // Sign out from Supabase
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.error('🔐 [AUTH STORE] Sign out error:', error);
      } else {
        console.log('🔐 [AUTH STORE] Sign out successful');
      }
      
      return { error };
    } catch (error) {
      console.error('🔐 [AUTH STORE] Sign out error:', error);
      this.updateState({ loading: false });
      return { error: error as any };
    }
  }

  // Get current state (for synchronous access)
  getState() {
    return this.state;
  }

  // Cleanup method for when the store is no longer needed
  destroy() {
    if (this.authSubscription) {
      this.authSubscription.unsubscribe();
      this.authSubscription = null;
    }
    this.listeners.clear();
    this.initialized = false;
    this.initPromise = null;
  }
}

// Export singleton instance
export const authStore = new AuthStore();
